/**
 * Reading of the file loaded on the input screen, in the browser: a plain text file
 * (`.txt`, UTF-8), a Word document (`.docx`) or a PDF with text. Only the text is kept.
 * The Word and PDF libraries are heavy, so they are imported on demand, the first time
 * a file of their format is read: the application does not carry them until then.
 */
import { recollerLignes } from '@/lib/recoller-lignes';
import { LONGUEUR_MAX_TEXTE, longueurTexte } from '@rosetta/shared';
import { type ElementTextePdf, textePageDepuisElements } from './paragraphes-pdf';

/** Largest file read; beyond it the file is refused before anything is read. */
export const TAILLE_MAX_FICHIER = 20 * 1024 * 1024;

/** A PDF whose pages together give fewer characters than this (spaces left out) has no text. */
export const MIN_CARACTERES_PDF = 20;

/** Shown under the text zone for a file of another format: the only message that names the formats. */
export const MESSAGE_FICHIER_REFUSE =
  "Ce fichier n'est pas pris en charge : chargez un document Word (.docx), un PDF ou un fichier texte.";

/** Shown for a file of a supported format that gave no text (scanned, protected, damaged). */
export const MESSAGE_FICHIER_ILLISIBLE = "Ce fichier n'a pas pu être lu.";

/** Shown for a file over `TAILLE_MAX_FICHIER`. */
export const MESSAGE_FICHIER_TROP_LOURD = 'Ce fichier dépasse 20 Mo.';

/**
 * Shown for a file whose text is longer than a translation may be (#13): it is not loaded at all,
 * never cut to fit.
 */
export const MESSAGE_FICHIER_TROP_LONG =
  "Ce fichier dépasse 10 000 caractères (environ 3 pages) : il n'a pas été chargé.";

const TYPE_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * What the file picker offers; the user can still pick anything, `formatDuFichier` decides.
 * Extensions only: on Windows, Chrome looks each MIME type up in the registry when the dialog
 * opens, which can delay it by seconds, and the extensions filter the same three formats.
 */
export const ACCEPT_FICHIER = '.txt,.docx,.pdf';

export type FormatFichier = 'txt' | 'docx' | 'pdf';

export type MotifErreurFichier = 'refuse' | 'illisible' | 'trop_lourd' | 'trop_long';

const MESSAGES: Record<MotifErreurFichier, string> = {
  refuse: MESSAGE_FICHIER_REFUSE,
  illisible: MESSAGE_FICHIER_ILLISIBLE,
  trop_lourd: MESSAGE_FICHIER_TROP_LOURD,
  trop_long: MESSAGE_FICHIER_TROP_LONG,
};

/** A file that cannot be loaded; its message is the one shown under the zone. */
export class ErreurFichier extends Error {
  readonly motif: MotifErreurFichier;

  constructor(motif: MotifErreurFichier, options?: ErrorOptions) {
    super(MESSAGES[motif], options);
    this.name = 'ErreurFichier';
    this.motif = motif;
  }
}

/** The format of a file, from its extension or else its MIME type; `null` when it is not read. */
export function formatDuFichier(fichier: File): FormatFichier | null {
  const nom = fichier.name.toLowerCase();
  if (nom.endsWith('.txt')) return 'txt';
  if (nom.endsWith('.docx')) return 'docx';
  if (nom.endsWith('.pdf')) return 'pdf';
  if (fichier.type === 'text/plain') return 'txt';
  if (fichier.type === TYPE_DOCX) return 'docx';
  if (fichier.type === 'application/pdf') return 'pdf';
  return null;
}

/**
 * Reads the text of a file. Rejects with an `ErreurFichier` and nothing else: the format
 * is not read (`refuse`), the file is over 20 Mo (`trop_lourd`), it gave no text, or
 * could not be opened (`illisible`), or its text is longer than a translation may be
 * (`trop_long`, #13: refused whole, never cut).
 */
export async function lireFichier(fichier: File): Promise<string> {
  const format = formatDuFichier(fichier);
  if (format === null) throw new ErreurFichier('refuse');
  if (fichier.size > TAILLE_MAX_FICHIER) throw new ErreurFichier('trop_lourd');

  let texte: string;
  try {
    texte = await LECTEURS[format](fichier);
  } catch (erreur) {
    // The user gets one message; the real cause is left in the console, where it can be found (#29).
    // biome-ignore lint/suspicious/noConsole: the only trace of why a file could not be read
    if (!(erreur instanceof ErreurFichier)) console.error('[lireFichier]', format, erreur);
    throw erreur instanceof ErreurFichier
      ? erreur
      : new ErreurFichier('illisible', { cause: erreur });
  }

  const vide =
    format === 'pdf' ? texte.replace(/\s/g, '').length < MIN_CARACTERES_PDF : texte.trim() === '';
  if (vide) throw new ErreurFichier('illisible');
  if (longueurTexte(texte) > LONGUEUR_MAX_TEXTE) throw new ErreurFichier('trop_long');
  return texte;
}

/** The content of a text file, decoded as UTF-8. */
export function lireFichierTexte(fichier: File): Promise<string> {
  return new Promise((resolu, rejete) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolu(String(lecteur.result));
    lecteur.onerror = () => rejete(lecteur.error ?? new Error('File could not be read'));
    lecteur.readAsText(fichier, 'UTF-8');
  });
}

/** The bytes of a file (`FileReader` rather than `Blob.arrayBuffer()`: it exists everywhere the screen runs). */
function lireOctets(fichier: File): Promise<ArrayBuffer> {
  return new Promise((resolu, rejete) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolu(lecteur.result as ArrayBuffer);
    lecteur.onerror = () => rejete(lecteur.error ?? new Error('File could not be read'));
    lecteur.readAsArrayBuffer(fichier);
  });
}

// ----------------------------------------------------------------------------------------
// Word

/** The part of a mammoth document node the transformation below needs. */
interface NoeudDocx {
  type: string;
  children?: NoeudDocx[];
}

/** The document without its note references: footnotes and endnotes are left out of the text. */
function sansNotes(noeud: NoeudDocx): NoeudDocx {
  if (noeud.children === undefined) return noeud;
  return {
    ...noeud,
    children: noeud.children.filter((enfant) => enfant.type !== 'noteReference').map(sansNotes),
  };
}

/** The text of an element, a `<br>` being a line break and a no-break space a space. */
function texteDe(element: Element): string {
  const copie = element.cloneNode(true) as Element;
  for (const saut of Array.from(copie.querySelectorAll('br'))) saut.replaceWith('\n');
  return (copie.textContent ?? '').replace(/ /g, ' ');
}

/** The items of a list, nested lists included, one text each. */
function collecterElements(liste: Element, elements: string[]): void {
  for (const li of Array.from(liste.children)) {
    const copie = li.cloneNode(true) as Element;
    const imbriquees = Array.from(li.children).filter(
      (e) => e.tagName === 'UL' || e.tagName === 'OL',
    );
    for (const sousListe of Array.from(copie.children).filter(
      (e) => e.tagName === 'UL' || e.tagName === 'OL',
    )) {
      sousListe.remove();
    }
    elements.push(texteDe(copie));
    for (const sousListe of imbriquees) collecterElements(sousListe, elements);
  }
}

/**
 * One block per paragraph or heading; one per list (its items, one per line); one per table (its
 * rows, one per line, the cells tab-separated). A cell is its paragraphs joined by a space.
 */
function collecterBlocs(parent: Element, blocs: string[]): void {
  for (const enfant of Array.from(parent.children)) {
    const balise = enfant.tagName;
    if (balise === 'TABLE') {
      const rangees = Array.from(enfant.querySelectorAll('tr')).map((rangee) =>
        Array.from(rangee.children)
          .map((cellule) => {
            const contenu: string[] = [];
            collecterBlocs(cellule, contenu);
            return (contenu.length > 0 ? contenu.join(' ') : texteDe(cellule)).trim();
          })
          .join('\t'),
      );
      blocs.push(rangees.join('\n'));
    } else if (balise === 'UL' || balise === 'OL') {
      const elements: string[] = [];
      collecterElements(enfant, elements);
      blocs.push(
        elements
          .map((e) => e.trim())
          .filter((e) => e !== '')
          .join('\n'),
      );
    } else if (balise === 'DIV') {
      collecterBlocs(enfant, blocs);
    } else {
      blocs.push(texteDe(enfant));
    }
  }
}

/**
 * The plain text of the HTML mammoth writes: a blank line between the paragraphs, headings,
 * lists and tables (the paragraphs of the document are the paragraphs of the text); a line break
 * only for a break wanted inside a paragraph, and between the items of a list or the rows of a table.
 */
export function htmlVersTexte(html: string): string {
  const document = new DOMParser().parseFromString(html, 'text/html');
  const blocs: string[] = [];
  collecterBlocs(document.body, blocs);
  return blocs
    .map((bloc) => bloc.trim())
    .filter((bloc) => bloc !== '')
    .join('\n\n');
}

async function lireDocx(fichier: File): Promise<string> {
  const mammoth = await import('mammoth');
  const arrayBuffer = await lireOctets(fichier);
  const { value } = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      // The images are ignored: nothing is read from them.
      convertImage: mammoth.images.imgElement(() => Promise.resolve({ src: '' })),
      transformDocument: sansNotes,
    },
  );
  return htmlVersTexte(value);
}

// ----------------------------------------------------------------------------------------
// PDF

async function lirePdf(fichier: File): Promise<string> {
  const [pdfjs, travailleur] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = travailleur.default;

  const data = new Uint8Array(await lireOctets(fichier));
  const tache = pdfjs.getDocument({ data, verbosity: pdfjs.VerbosityLevel.ERRORS });

  try {
    const document = await tache.promise;
    const pages: string[] = [];
    for (let numero = 1; numero <= document.numPages; numero++) {
      const page = await document.getPage(numero);
      const contenu = await page.getTextContent();
      const texte = textePageDepuisElements(
        contenu.items.flatMap((element): ElementTextePdf[] => ('str' in element ? [element] : [])),
      );
      const net = texte.trim();
      if (net !== '') pages.push(net);
    }
    // Pages are separated by an empty line.
    return pages.join('\n\n');
  } finally {
    await tache.destroy();
  }
}

const LECTEURS: Record<FormatFichier, (fichier: File) => Promise<string>> = {
  txt: async (fichier) => recollerLignes(await lireFichierTexte(fichier)),
  docx: lireDocx,
  pdf: lirePdf,
};
